import { Box, Button, CardMedia, Typography } from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { memo, ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface BoundingBox {
  rel_x1: number;
  rel_y1: number;
  rel_x2: number;
  rel_y2: number;
  color: 'warning' | 'danger';
  horizontalLabel?: string;
  verticalLabel?: string;
}

interface ImageCardProps {
  imageSrc: string;
  downloadFileName?: string;
  boundingBoxes?: BoundingBox[];
}

export const ImageCard = memo(function ({ imageSrc, downloadFileName, boundingBoxes }: ImageCardProps): ReactElement {
  const { t } = useTranslation();
  const imageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [imgDimensions, setImgDimensions] = useState({ width: 0, height: 0, offsetX: 0, offsetY: 0 });
  const [hoveredBoundingBoxIndex, setHoveredBoundingBoxIndex] = useState<number | null>(null);

  const recalcImageDimensions = useCallback(() => {
    if (!imageRef.current || !containerRef.current) return;

    const imgWidth = imageRef.current.clientWidth;
    const imgHeight = imageRef.current.clientHeight;
    const imgContainerWidth = containerRef.current.clientWidth;
    const imgContainerHeight = containerRef.current.clientHeight;

    setImgDimensions({
      width: imgWidth,
      height: imgHeight,
      offsetX: (imgContainerWidth - imgWidth) / 2,
      offsetY: (imgContainerHeight - imgHeight) / 2,
    });
  }, []);

  useEffect(() => {
    const handleResize = () => recalcImageDimensions();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [recalcImageDimensions]);

  const handleDownload = useCallback(() => {
    if (!downloadFileName) return;

    const image = imageRef.current;
    let downloadSource = imageSrc;

    if (image?.naturalWidth && image.naturalHeight) {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = image.naturalWidth;
        canvas.height = image.naturalHeight;
        const context = canvas.getContext('2d');
        if (context) {
          context.fillStyle = '#fff';
          context.fillRect(0, 0, canvas.width, canvas.height);
          context.drawImage(image, 0, 0, canvas.width, canvas.height);
          drawBoundingBoxes(context, boundingBoxes ?? [], {
            width: canvas.width,
            height: canvas.height,
            styleScale: 2.5,
          });
          downloadSource = canvas.toDataURL('image/jpeg', 0.92);
        }
      } catch {
        // Fall through to default image download
      }
    }

    downloadImage(downloadSource, downloadFileName);
  }, [boundingBoxes, downloadFileName, imageSrc]);

  return (
    <Box
      display='flex'
      flexDirection='column'
      alignItems='center'
      height='100%'
      boxSizing='border-box'
      gap={2}
      padding={2}
    >
      <Box
        ref={containerRef}
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          overflow: 'hidden',
          position: 'relative',
          minHeight: 0,
        }}
      >
        <CardMedia
          ref={imageRef}
          component='img'
          src={imageSrc}
          draggable={false}
          sx={{
            objectFit: 'contain',
            maxHeight: '100%',
            maxWidth: '100%',
            borderRadius: '10px',
            pointerEvents: 'none',
            userSelect: 'none',
          }}
          onLoad={recalcImageDimensions}
        />
        {boundingBoxes &&
          boundingBoxes.map((bb, index) => (
            <BoundingBoxOverlay
              key={`bb-${index}`}
              imgDimensions={imgDimensions}
              boundingBox={bb}
              isActive={hoveredBoundingBoxIndex === null || hoveredBoundingBoxIndex === index}
              isHovered={hoveredBoundingBoxIndex === index}
              onHoverStart={() => setHoveredBoundingBoxIndex(index)}
              onHoverEnd={() => setHoveredBoundingBoxIndex((current) => (current === index ? null : current))}
            />
          ))}
      </Box>
      {downloadFileName && (
        <Button
          variant='contained'
          onClick={(event) => {
            event.stopPropagation();
            handleDownload();
          }}
          startIcon={<FileDownloadIcon />}
        >
          {t('common.download')}
        </Button>
      )}
    </Box>
  );
});

function downloadImage(imageSrc: string, downloadFileName: string) {
  const link = document.createElement('a');
  link.href = imageSrc;
  link.download = downloadFileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

function drawBoundingBoxes(
  context: CanvasRenderingContext2D,
  boundingBoxes: BoundingBox[],
  image: { width: number; height: number; styleScale: number }
) {
  boundingBoxes.forEach((boundingBox) => {
    const x1Display = boundingBox.rel_x1 * image.width;
    const y1Display = boundingBox.rel_y1 * image.height;
    const x2Display = boundingBox.rel_x2 * image.width;
    const y2Display = boundingBox.rel_y2 * image.height;
    const widthDisplay = x2Display - x1Display;
    const heightDisplay = y2Display - y1Display;
    const overlayColor = boundingBoxColor(boundingBox.color);

    context.save();
    context.strokeStyle = overlayColor;
    context.lineWidth = Math.max(1, image.styleScale);
    roundedRect(context, x1Display, y1Display, widthDisplay, heightDisplay, 3 * image.styleScale);
    context.stroke();
    context.restore();

    const horizontalLabelPlacement = getHorizontalLabelPlacement({
      xCenter: x1Display + widthDisplay / 2,
      y: y1Display,
      imageLeft: 0,
      imageTop: 0,
      imageWidth: image.width,
      scale: image.styleScale,
    });
    const verticalLabelPlacement = getVerticalLabelPlacement({
      x: x1Display,
      yCenter: y1Display + heightDisplay / 2,
      imageLeft: 0,
      imageTop: 0,
      imageHeight: image.height,
      scale: image.styleScale,
    });

    if (boundingBox.horizontalLabel) {
      drawEdgeValueLabel(context, {
        text: boundingBox.horizontalLabel,
        left: horizontalLabelPlacement.left,
        top: horizontalLabelPlacement.top,
        transform: horizontalLabelPlacement.transform,
        styleScale: image.styleScale,
      });
    }

    if (boundingBox.verticalLabel) {
      drawEdgeValueLabel(context, {
        text: boundingBox.verticalLabel,
        left: verticalLabelPlacement.left,
        top: verticalLabelPlacement.top,
        transform: verticalLabelPlacement.transform,
        styleScale: image.styleScale,
        rotation: 'vertical',
      });
    }
  });
}

function drawEdgeValueLabel(
  context: CanvasRenderingContext2D,
  {
    text,
    left,
    top,
    transform,
    styleScale,
    rotation = 'horizontal',
  }: {
    text: string;
    left: number;
    top: number;
    transform: string;
    styleScale: number;
    rotation?: 'horizontal' | 'vertical';
  }
) {
  const fontSize = 11 * styleScale;
  const paddingX = (rotation === 'horizontal' ? 3 : 4) * styleScale;
  const paddingY = 1 * styleScale;
  const labelHeight = fontSize * 0.9 + paddingY * 2;

  context.save();
  context.font = `${fontSize}px Roboto, Arial, sans-serif`;
  const labelWidth = context.measureText(text).width + paddingX * 2;

  if (rotation === 'vertical') {
    const centerX = transform === 'translate(-100%, -50%)' ? left - labelWidth / 2 : left + labelWidth / 2;

    context.translate(centerX, top);
    context.rotate(-Math.PI / 2);
    drawLabelBackground(context, -labelWidth / 2, -labelHeight / 2, labelWidth, labelHeight, styleScale);
    drawLabelText(context, text, -labelWidth / 2, -labelHeight / 2, labelWidth, labelHeight, paddingX);
    context.restore();
    return;
  }

  const x = left - labelWidth / 2;
  const y = transform === 'translate(-50%, -100%)' ? top - labelHeight : top;
  drawLabelBackground(context, x, y, labelWidth, labelHeight, styleScale);
  drawLabelText(context, text, x, y, labelWidth, labelHeight, paddingX);
  context.restore();
}

function drawLabelBackground(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  styleScale: number
) {
  context.save();
  context.fillStyle = 'rgba(255, 255, 255, 0.7)';
  context.shadowColor = 'rgba(0, 0, 0, 0.28)';
  context.shadowOffsetY = styleScale;
  context.shadowBlur = 2 * styleScale;
  roundedRect(context, x, y, width, height, 4 * styleScale);
  context.fill();
  context.restore();
}

function drawLabelText(
  context: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  width: number,
  height: number,
  paddingX: number
) {
  context.fillStyle = '#000';
  context.textBaseline = 'middle';
  context.fillText(text, x + paddingX, y + height / 2, width - paddingX * 2);
}

function roundedRect(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number
) {
  const safeRadius = Math.min(radius, Math.abs(width) / 2, Math.abs(height) / 2);
  context.beginPath();
  context.moveTo(x + safeRadius, y);
  context.lineTo(x + width - safeRadius, y);
  context.quadraticCurveTo(x + width, y, x + width, y + safeRadius);
  context.lineTo(x + width, y + height - safeRadius);
  context.quadraticCurveTo(x + width, y + height, x + width - safeRadius, y + height);
  context.lineTo(x + safeRadius, y + height);
  context.quadraticCurveTo(x, y + height, x, y + height - safeRadius);
  context.lineTo(x, y + safeRadius);
  context.quadraticCurveTo(x, y, x + safeRadius, y);
  context.closePath();
}

function boundingBoxColor(col: 'danger' | 'warning') {
  switch (col) {
    case 'danger':
      return 'red';
    default:
      return 'yellow';
  }
}

interface BoundingBoxProps {
  imgDimensions: { width: number; height: number; offsetX: number; offsetY: number };
  boundingBox: BoundingBox;
  isActive: boolean;
  isHovered: boolean;
  onHoverStart: () => void;
  onHoverEnd: () => void;
}

const BoundingBoxOverlay = ({
  imgDimensions,
  boundingBox,
  isActive,
  isHovered,
  onHoverStart,
  onHoverEnd,
}: BoundingBoxProps): ReactElement => {
  const x1Display = boundingBox.rel_x1 * imgDimensions.width + imgDimensions.offsetX;
  const y1Display = boundingBox.rel_y1 * imgDimensions.height + imgDimensions.offsetY;
  const x2Display = boundingBox.rel_x2 * imgDimensions.width + imgDimensions.offsetX;
  const y2Display = boundingBox.rel_y2 * imgDimensions.height + imgDimensions.offsetY;
  const widthDisplay = x2Display - x1Display;
  const heightDisplay = y2Display - y1Display;

  const overlayColor = boundingBoxColor(boundingBox.color);
  const horizontalLabelPlacement = getHorizontalLabelPlacement({
    xCenter: x1Display + widthDisplay / 2,
    y: y1Display,
    imageLeft: imgDimensions.offsetX,
    imageTop: imgDimensions.offsetY,
    imageWidth: imgDimensions.width,
  });
  const verticalLabelPlacement = getVerticalLabelPlacement({
    x: x1Display,
    yCenter: y1Display + heightDisplay / 2,
    imageLeft: imgDimensions.offsetX,
    imageTop: imgDimensions.offsetY,
    imageHeight: imgDimensions.height,
  });

  return (
    <>
      <Box
        sx={{
          position: 'absolute',
          top: y1Display,
          left: x1Display,
          width: `${widthDisplay}px`,
          height: `${heightDisplay}px`,
          border: `1px solid ${overlayColor}`,
          borderRadius: '3px',
          cursor: 'pointer',
          zIndex: isHovered ? 2 : 1,
          opacity: isActive ? 1 : 0.25,
          transition: 'opacity 160ms ease',
          pointerEvents: isActive ? 'auto' : 'none',
        }}
        onMouseEnter={onHoverStart}
        onMouseLeave={onHoverEnd}
      />
      {boundingBox.horizontalLabel && (
        <EdgeValueLabel
          text={boundingBox.horizontalLabel}
          left={horizontalLabelPlacement.left}
          top={horizontalLabelPlacement.top}
          transform={horizontalLabelPlacement.transform}
          isVisible={isActive}
        />
      )}
      {boundingBox.verticalLabel && (
        <EdgeValueLabel
          text={boundingBox.verticalLabel}
          left={verticalLabelPlacement.left}
          top={verticalLabelPlacement.top}
          transform={verticalLabelPlacement.transform}
          isVisible={isActive}
          rotation='vertical'
        />
      )}
    </>
  );
};

function getHorizontalLabelPlacement({
  xCenter,
  y,
  imageLeft,
  imageTop,
  imageWidth,
  scale = 1,
}: {
  xCenter: number;
  y: number;
  imageLeft: number;
  imageTop: number;
  imageWidth: number;
  scale?: number;
}) {
  const horizontalPadding = 16 * scale;
  const hasSpaceAbove = y - imageTop >= 26 * scale;

  return {
    left: clamp(xCenter + 1 * scale, imageLeft + horizontalPadding, imageLeft + imageWidth - horizontalPadding),
    top: hasSpaceAbove ? y - 0 : y + 6 * scale,
    transform: hasSpaceAbove ? 'translate(-50%, -100%)' : 'translate(-50%, 0)',
  };
}

function getVerticalLabelPlacement({
  x,
  yCenter,
  imageLeft,
  imageTop,
  imageHeight,
  scale = 1,
}: {
  x: number;
  yCenter: number;
  imageLeft: number;
  imageTop: number;
  imageHeight: number;
  scale?: number;
}) {
  const verticalPadding = 16 * scale;
  const hasSpaceLeft = x - imageLeft >= 52 * scale;

  return {
    left: hasSpaceLeft ? x + 8 * scale : x + 5 * scale,
    top: clamp(yCenter - 1 * scale, imageTop + verticalPadding, imageTop + imageHeight - verticalPadding),
    transform: hasSpaceLeft ? 'translate(-100%, -50%)' : 'translate(0, -50%)',
  };
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function EdgeValueLabel({
  text,
  left,
  top,
  transform,
  isVisible,
  rotation = 'horizontal',
}: {
  text: string;
  left: number;
  top: number;
  transform: string;
  isVisible: boolean;
  rotation?: 'horizontal' | 'vertical';
}) {
  return (
    <Box
      sx={{
        position: 'absolute',
        left,
        top,
        transform,
        zIndex: 1,
        pointerEvents: 'none',
        opacity: isVisible ? 1 : 0,
        transition: 'opacity 160ms ease',
      }}
    >
      <Box
        sx={{
          borderRadius: 1,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: rotation === 'horizontal' ? 0.25 : 0.5,
          py: 0.125,
          backgroundColor: 'rgba(255, 255, 255, 0.7)',
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.28)',
          transform: rotation === 'vertical' ? 'rotate(-90deg)' : 'none',
          transformOrigin: 'center',
        }}
      >
        <Typography variant='caption' noWrap sx={{ display: 'block', lineHeight: 0.9, fontSize: 11 }}>
          {text}
        </Typography>
      </Box>
    </Box>
  );
}
